import { useState, useEffect, useContext, useRef } from "react";
import { Navigate, useParams, Link } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import AppBackService from "../services/appback";
import { RootContext } from "../context/root-provider";

const acceptErrorMessage = (res) => {
  if (res?.status === 404) return "This invitation no longer exists. The owner may have cancelled it.";
  if (res?.status === 403)
    return "This invitation was sent to a different account. Sign in with the address it was sent to.";
  if (res?.status === 409) return "This invitation was already accepted or declined.";
  return "Couldn't accept the invitation. Try again from your documents.";
};

/**
 * Landing page for share-invite email links: /share/accept/:shareId (TT-807).
 * Accepts the invitation and opens the shared document.
 */
const ShareAcceptPage = () => {
  const { shareId } = useParams();
  const { userInfo, loggingIn } = useContext(RootContext);
  const [status, setStatus] = useState("working");
  const [message, setMessage] = useState("");
  const [docId, setDocId] = useState(null);
  // One accept per share id: a second call (StrictMode's double effect, a
  // re-render mid-flight) would 409 on the share the first one just took.
  const attempted = useRef(null);

  useEffect(() => {
    if (!userInfo?.username || loggingIn || attempted.current === shareId) return;
    attempted.current = shareId;
    (async () => {
      const res = await AppBackService.acceptShare(userInfo.username, shareId);
      if (res && !res.error) {
        // axiosExc camelizes top-level keys only; the share row stays snake_case
        setDocId(res.share?.doc_id || null);
        setStatus("done");
      } else {
        setStatus("error");
        setMessage(acceptErrorMessage(res));
      }
    })();
  }, [loggingIn, shareId, userInfo?.username]);

  if (status === "done") {
    return <Navigate to={docId ? `/document/${docId}` : "/"} replace />;
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", paddingTop: "80px" }}>
      {status === "working" ? (
        <>
          <CircularProgress />
          <Typography>Accepting shared document...</Typography>
        </>
      ) : (
        <>
          <Typography sx={{ maxWidth: "480px", textAlign: "center" }}>{message}</Typography>
          <Link to="/">Back to your documents</Link>
        </>
      )}
    </Box>
  );
};

export default ShareAcceptPage;
